import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import { ThemeProvider } from '@context/ThemeContext';
import { UserProvider } from '@common/contexts/UserContext';
import { ToastProvider } from '@common/contexts/ToastContext';
import { ProtectedRoute, ErrorBoundary } from '@common/components';
import i18n from '@i18n';
import ReportsLayout from './components/ReportsLayout/ReportsLayout';
import ReportsPage from './components/ReportsPage';
import ReportCreatePage from './components/ReportCreatePage/ReportCreatePage';
import ReportEditPage from './components/ReportEditPage/ReportEditPage';
import ReportConfigurePage from './components/ReportConfigurePage/ReportConfigurePage';
import ReportViewPage from './components/ReportViewPage/ReportViewPage';
import ScheduleReportPage from './components/ScheduleReportPage/ScheduleReportPage';
import './styles.css';
import { APP_NAME } from './constants';

// Create a React Query client with default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000, // 30 seconds
    },
  },
});

// Get basename from current pathname (handles both dev and prod paths)
const getBasename = () => {
  const { pathname } = window.location;
  // Remove trailing slash and any sub-routes
  const regex = new RegExp(`(.*/${APP_NAME})`);
  const match = pathname.match(regex);
  return match ? match[1] : `/${APP_NAME}`;
};

function App() {
  return (
    <BrowserRouter basename={getBasename()}>
      <I18nextProvider i18n={i18n}>
        <ThemeProvider>
          <ToastProvider>
            <QueryClientProvider client={queryClient}>
              <UserProvider>
                <ProtectedRoute redirectPath="/login/index.html">
                  <ErrorBoundary>
                    <Routes>
                      <Route element={<ReportsLayout />}>
                        <Route path="/" element={<ReportsPage />} />
                        <Route path="/saved" element={<ReportsPage initialTab="saved" />} />
                        <Route path="/scheduled" element={<ReportsPage initialTab="scheduled" />} />
                        <Route path="/schedule/new" element={<ScheduleReportPage />} />
                        <Route path="/schedule/:id/edit" element={<ScheduleReportPage />} />
                        <Route path="/create" element={<ReportCreatePage />} />
                        <Route path="/:id/edit" element={<ReportEditPage />} />
                        <Route path="/:id/configure" element={<ReportConfigurePage />} />
                        <Route path="/:id/view" element={<ReportViewPage />} />
                      </Route>
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </ErrorBoundary>
                </ProtectedRoute>
              </UserProvider>
            </QueryClientProvider>
          </ToastProvider>
        </ThemeProvider>
      </I18nextProvider>
    </BrowserRouter>
  );
}

const container = document.getElementById('root');
const root = createRoot(container);
root.render(<App />);
